import {
  Badge,
  Box,
  Button,
  Chip,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  applyFilters,
  formatDateRange,
  formatRelative,
  groupByRecency,
} from '../../components/inbox/helpers';
import {
  FILTER_VALUES,
  type FilterValue,
  type InboxSearch,
  type InboxThread,
  type ThreadGroup,
} from '../../components/inbox/types';
import { StateBadge } from '../../components/StateBadge';
import { EmptyState } from '../../components/ui';
import { useAuth } from '../../lib/auth-context';
import type { AnyState } from '../../lib/display-state';
import { track } from '../../lib/telemetry';
import { ThreadView } from './$threadId';

/**
 * Inbox layout constants. The inbox is the one authenticated surface that
 * deliberately escapes the 1280px content frame to behave like a productivity
 * app (Gmail / Linear / Slack). The two-pane region fills the viewport
 * vertically so the list and the active thread both scroll inside fixed
 * scrollports rather than the page scrolling as a whole.
 *
 * Height math: viewport - AppHeader (64) - inbox header strip (~72) = TwoPane.
 * The constant is intentionally conservative; the actual strip height varies
 * a hair with chip wrapping, so we let flexbox absorb a few pixels of give.
 */
const INBOX_HORIZONTAL_GUTTER = 'clamp(16px, 4vw, 32px)';
const INBOX_HEADER_OFFSET = 64; // AppHeader minHeight from HeaderShell.
const INBOX_STRIP_OFFSET = 80; // approx of strip + 1px border + breathing.

/**
 * /inbox — thread list, with a persistent two-pane on desktop.
 *
 * Desktop (≥ lg): clicking a thread updates `?t=<threadId>` and renders the
 * thread inline in the right pane via the shared <ThreadView> component
 * (factored out of /inbox/$threadId). The current URL stays at /inbox so the
 * filter chips and search box hold their place.
 *
 * Mobile (< lg): clicking a thread navigates to /inbox/$threadId on its own
 * screen — full-bleed message stream + back arrow.
 *
 * Search params:
 *   - t  : selected thread id (desktop only; ignored on narrow screens)
 *   - f  : filter chip — 'all' | 'unread' | 'active' | 'past'
 *
 * The list itself is one fetch — counterparty + last-message preview +
 * unread are joined server-side so the SPA doesn't N+1.
 */


async function fetchInbox(): Promise<{ threads: InboxThread[] }> {
  const res = await fetch('/api/threads', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) {
    if (res.status === 401) throw new Error('unauthenticated');
    throw new Error(`inbox load failed: ${res.status}`);
  }
  return (await res.json()) as { threads: InboxThread[] };
}

function InboxList() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { activeHat } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['inbox'],
    queryFn: fetchInbox,
    refetchOnWindowFocus: true,
  });

  const allThreads = data?.threads ?? [];
  const totalUnread = allThreads.reduce((sum, t) => sum + t.unreadCount, 0);

  // Default the filter to 'unread' if any thread is unread and no explicit
  // selection has been made; otherwise 'all'. We don't write this back to
  // the URL — the user has to click a chip to persist.
  const filter: FilterValue =
    search.f !== undefined ? search.f : totalUnread > 0 && allThreads.length > 0 ? 'unread' : 'all';

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, 200);

  // Telemetry — emit `inbox.search_used` once per debounced settle when the
  // term has actual content. Avoid firing the initial empty value or on every
  // re-render with the same term.
  const lastSearchEmitRef = useRef<string>('');
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (trimmed.length === 0) return;
    if (lastSearchEmitRef.current === trimmed) return;
    lastSearchEmitRef.current = trimmed;
    const bucket: 'short' | 'med' | 'long' =
      trimmed.length <= 3 ? 'short' : trimmed.length <= 10 ? 'med' : 'long';
    track('inbox.search_used', { queryLengthBucket: bucket });
  }, [debouncedSearch]);

  // Apply filter chip + search.
  const filtered = useMemo(
    () => applyFilters(allThreads, filter, debouncedSearch),
    [allThreads, filter, debouncedSearch],
  );

  const groups = useMemo(() => groupByRecency(filtered), [filtered]);

  const setFilter = (next: FilterValue) => {
    track('inbox.filter_chip_clicked', { chip: next });
    // Always persist the explicit chip — including 'all'. Without `f=all` in
    // the URL, the default-resolution above falls back to 'unread' whenever
    // any thread is unread, which makes the "All" chip appear unclickable.
    const out: InboxSearch = { f: next };
    if (search.t !== undefined) out.t = search.t;
    void navigate({ to: '/inbox', search: out, replace: true });
  };

  const setSelected = (threadId: string | null) => {
    const out: InboxSearch = {};
    if (threadId !== null) out.t = threadId;
    if (search.f !== undefined) out.f = search.f;
    void navigate({ to: '/inbox', search: out, replace: true });
  };

  // If the selected thread isn't in the filtered list, keep the URL as-is —
  // the user may be filtering after selecting; on filter change we don't
  // want to drop their selection silently. The right pane just keeps
  // showing the previously selected thread until they pick another.
  const selectedId = search.t;

  // FR-084: gate the desktop two-pane on a REAL media query, not CSS-only
  // `visibleFrom`. Under `visibleFrom` the hidden pane still mounts (Mantine's
  // documented double-mount trap), so on a phone — or a desktop tab resized
  // below lg while a thread is open, or a `?t=` link opened on mobile — the
  // invisible ThreadView still fetched the thread, opened a live ThreadDO
  // WebSocket, and POSTed read receipts for messages the user never saw. Read
  // ticks are socially load-bearing here, so a hidden pane must not emit them.
  // No SSR in this SPA, so read the true value on first paint (no flash).
  const isDesktop = useMediaQuery('(min-width: 75em)', false, {
    getInitialValueInEffect: false,
  });

  return (
    <AppShell tone="full" compact>
      <InboxHeaderStrip
        totalUnread={totalUnread}
        filter={filter}
        onFilterChange={setFilter}
        search={searchInput}
        onSearchChange={setSearchInput}
      />

      {isDesktop ? (
        <Box
          style={{
            paddingInline: INBOX_HORIZONTAL_GUTTER,
            paddingBlock: 'var(--mantine-spacing-md)',
            height: `calc(100vh - ${INBOX_HEADER_OFFSET}px - ${INBOX_STRIP_OFFSET}px)`,
            minHeight: 560,
          }}
        >
          <TwoPane
            groups={groups}
            loading={isLoading}
            error={error}
            filter={filter}
            totalCount={allThreads.length}
            totalUnread={totalUnread}
            selectedId={selectedId}
            onSelect={setSelected}
            activeHat={activeHat}
          />
        </Box>
      ) : (
        <Box
          style={{
            paddingInline: INBOX_HORIZONTAL_GUTTER,
            paddingBlock: 'var(--mantine-spacing-md)',
          }}
        >
          <ListPane
            groups={groups}
            loading={isLoading}
            error={error}
            filter={filter}
            totalCount={allThreads.length}
            navigateOnClick
            selectedId={null}
            onSelect={() => {}}
            activeHat={activeHat}
          />
        </Box>
      )}
    </AppShell>
  );
}

/**
 * Compact inbox header strip. Replaces the legacy PageHero on the inbox
 * surface — productivity apps don't get a tall hero, they get a thin band
 * that holds the title, an unread count, and the filter + search controls.
 * Spans the full viewport width with `INBOX_HORIZONTAL_GUTTER` matching the
 * AppHeader's own gutters so the strip feels continuous with the chrome.
 */
function InboxHeaderStrip({
  totalUnread,
  filter,
  onFilterChange,
  search,
  onSearchChange,
}: {
  totalUnread: number;
  filter: FilterValue;
  onFilterChange: (next: FilterValue) => void;
  search: string;
  onSearchChange: (next: string) => void;
}) {
  return (
    <Box
      style={{
        paddingInline: INBOX_HORIZONTAL_GUTTER,
        paddingBlock: 'var(--mantine-spacing-md)',
        borderBottom: '1px solid var(--mantine-color-ink-1)',
        background:
          'linear-gradient(180deg, light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6)) 0%, transparent 120%)',
      }}
    >
      <Group justify="space-between" align="center" gap="md" wrap="wrap">
        <Group gap="sm" align="center" wrap="nowrap">
          <Title order={2} style={{ margin: 0, fontSize: 'clamp(1.25rem, 2vw, 1.5rem)' }}>
            Inbox
          </Title>
          {totalUnread > 0 ? (
            <Badge color="brand" variant="filled" size="md" radius="sm">
              {totalUnread} unread
            </Badge>
          ) : null}
        </Group>
        <FilterBar
          filter={filter}
          onFilterChange={onFilterChange}
          search={search}
          onSearchChange={onSearchChange}
        />
      </Group>
    </Box>
  );
}

function FilterBar({
  filter,
  onFilterChange,
  search,
  onSearchChange,
}: {
  filter: FilterValue;
  onFilterChange: (next: FilterValue) => void;
  search: string;
  onSearchChange: (next: string) => void;
}) {
  return (
    <Group justify="space-between" align="center" wrap="wrap" gap="sm">
      <Chip.Group
        multiple={false}
        value={filter}
        onChange={(v) => {
          if (typeof v === 'string' && (FILTER_VALUES as ReadonlyArray<string>).includes(v)) {
            onFilterChange(v as FilterValue);
          }
        }}
      >
        <Group gap="xs">
          <Chip value="all" size="md" color="brand" variant={filter === 'all' ? 'filled' : 'light'}>
            All
          </Chip>
          <Chip
            value="unread"
            size="md"
            color="brand"
            variant={filter === 'unread' ? 'filled' : 'light'}
          >
            Unread
          </Chip>
          <Chip
            value="active"
            size="md"
            color="brand"
            variant={filter === 'active' ? 'filled' : 'light'}
          >
            Active
          </Chip>
          <Chip value="past" size="md" color="brand" variant={filter === 'past' ? 'filled' : 'light'}>
            Past
          </Chip>
        </Group>
      </Chip.Group>
      <TextInput
        value={search}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        placeholder="Search names…"
        size="sm"
        w={{ base: '100%', sm: 240 }}
        leftSection={<SearchIcon />}
        aria-label="Search inbox"
      />
    </Group>
  );
}

function SearchIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/** 1.6-stroke star, house style. Decorative — replaces the unicode ★ glyph
 *  in the review-score triage badge. */
function StarIcon({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M12 3l2.6 5.6 6.1.7-4.5 4.2 1.2 6L12 18.7 6.6 19.5l1.2-6L3.3 9.3l6.1-.7z" />
    </svg>
  );
}

function TwoPane({
  groups,
  loading,
  error,
  filter,
  totalCount,
  totalUnread,
  selectedId,
  onSelect,
  activeHat,
}: {
  groups: ThreadGroup[];
  loading: boolean;
  error: unknown;
  filter: FilterValue;
  totalCount: number;
  totalUnread: number;
  selectedId: string | undefined;
  onSelect: (id: string | null) => void;
  activeHat: ReturnType<typeof useAuth>['activeHat'];
}) {
  // 100% fills the calc'd parent height from <InboxList>. The list and the
  // thread each get their own scrollport (overflowY: auto) so the page itself
  // never scrolls — productivity-inbox feel.
  return (
    <Group
      align="stretch"
      gap="md"
      wrap="nowrap"
      style={{ height: '100%' }}
    >
      <Box
        style={{
          flex: '0 0 380px',
          overflowY: 'auto',
          height: '100%',
          padding: 10,
          background: 'var(--mantine-color-body)',
          border: '1px solid var(--mantine-color-ink-1)',
          borderRadius: 16,
          // PANEL elevation token — deliberate depth on the list pane.
          boxShadow:
            'light-dark(0 1px 3px rgba(33,28,22,0.05), 0 1px 3px rgba(0,0,0,0.45)), light-dark(0 12px 32px rgba(33,28,22,0.07), 0 12px 32px rgba(0,0,0,0.32))',
        }}
      >
        <ListPane
          fillHeight
          groups={groups}
          loading={loading}
          error={error}
          filter={filter}
          totalCount={totalCount}
          navigateOnClick={false}
          selectedId={selectedId ?? null}
          onSelect={onSelect}
          activeHat={activeHat}
        />
      </Box>
      <Box
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          borderLeft: '1px solid var(--mantine-color-ink-1)',
          paddingLeft: 'var(--mantine-spacing-md)',
          overflowY: 'auto',
          height: '100%',
        }}
      >
        {/* Cap the reading column inside the right pane at ~880px with auto
            margins so messages don't stretch across an ultra-wide monitor.
            Mirrors how Gmail / Linear handle their thread surface. */}
        <Box style={{ maxWidth: 880, marginInline: 'auto', height: '100%' }}>
          {selectedId !== undefined && selectedId.length > 0 ? (
            <ThreadView key={selectedId} threadId={selectedId} embedded />
          ) : (
            <NoSelectionPane
              totalCount={totalCount}
              totalUnread={totalUnread}
              activeHat={activeHat}
            />
          )}
        </Box>
      </Box>
    </Group>
  );
}

function NoSelectionPane({
  totalCount,
  totalUnread,
  activeHat,
}: {
  totalCount: number;
  totalUnread: number;
  activeHat: ReturnType<typeof useAuth>['activeHat'];
}) {
  if (totalCount === 0) {
    const hostCta = activeHat === 'host';
    return (
      <Box
        py={56}
        px="md"
        style={{
          height: '100%',
          minHeight: 480,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'radial-gradient(circle at center, light-dark(var(--mantine-color-brand-0), var(--wwaave-brand-border)) 0%, transparent 70%)',
          borderRadius: 16,
        }}
      >
        <EmptyState
          title="No conversations yet."
          body="Threads land here once you start sending or receiving requests."
          action={
            <Button
              component={Link}
              to={hostCta ? '/host/events' : '/browse'}
              color="brand.6"
            >
              {hostCta ? 'Open events' : 'Browse venues'}
            </Button>
          }
        />
      </Box>
    );
  }
  return (
    <Box
      py={56}
      px="md"
      style={{
        height: '100%',
        minHeight: 480,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(circle at center, light-dark(var(--mantine-color-brand-0), var(--wwaave-brand-border)) 0%, transparent 70%)',
        borderRadius: 16,
      }}
    >
      <Stack gap={4} align="center">
        <Text fw={600} size="lg">
          Pick a conversation on the left.
        </Text>
        <Text size="sm" c="ink.6">
          {totalCount} {totalCount === 1 ? 'thread' : 'threads'}
          {totalUnread > 0
            ? `, ${totalUnread} unread`
            : ''}
          .
        </Text>
      </Stack>
    </Box>
  );
}

function ListPane({
  groups,
  loading,
  error,
  filter,
  totalCount,
  navigateOnClick,
  selectedId,
  onSelect,
  activeHat,
  fillHeight = false,
}: {
  groups: ThreadGroup[];
  loading: boolean;
  error: unknown;
  filter: FilterValue;
  totalCount: number;
  navigateOnClick: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  activeHat: ReturnType<typeof useAuth>['activeHat'];
  /**
   * When true (used by the desktop TwoPane), drop the legacy
   * `ScrollArea.Autosize mah={720}` and let the parent scrollport own
   * vertical overflow. Keeps the list flush with the calc'd viewport height.
   */
  fillHeight?: boolean;
}) {
  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }
  if (error !== null && error !== undefined) {
    return (
      <EmptyState
        title="Couldn't load your inbox."
        body="Refresh the page to try again."
      />
    );
  }
  if (totalCount === 0) {
    const hostCta = activeHat === 'host';
    return (
      <EmptyState
        title="No conversations yet."
        body="Threads land here once you start sending or receiving requests."
        action={
          <Button
            component={Link}
            to={hostCta ? '/host/events' : '/browse'}
            color="brand.6"
          >
            {hostCta ? 'Open events' : 'Browse venues'}
          </Button>
        }
      />
    );
  }
  if (groups.length === 0) {
    return (
      <EmptyState
        title="All caught up here."
        body={
          filter === 'unread'
            ? 'No unread threads. Switch to All to see the rest.'
            : filter === 'active'
              ? 'No active exchanges right now. Browse venues to get one started.'
              : filter === 'past'
                ? "No past exchanges yet. Your road's just starting."
                : 'Try a different filter or clear the search.'
        }
      />
    );
  }
  let runningIndex = 0;
  const listStack = (
    <Stack gap="md" pr={4}>
      {groups.map((g) => (
        <Stack key={g.label} gap={4}>
          <Box
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background:
                'linear-gradient(180deg, var(--mantine-color-body) 0%, var(--mantine-color-body) 70%, transparent 100%)',
              paddingTop: 4,
              paddingBottom: 4,
            }}
          >
            <Text
              size="xs"
              fw={700}
              c="brand.7"
              tt="uppercase"
              style={{ fontSize: 11, letterSpacing: '0.12em' }}
            >
              {g.label}
            </Text>
          </Box>
          <Stack gap={6}>
            {g.items.map((t) => {
              const idx = runningIndex++;
              return (
                <ThreadRow
                  key={t.threadId}
                  t={t}
                  selected={t.threadId === selectedId}
                  navigateOnClick={navigateOnClick}
                  onSelect={onSelect}
                  rowIndex={idx}
                />
              );
            })}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );

  // Desktop two-pane: parent Box owns vertical overflow, so just return the
  // stack. Mobile single-pane: keep the legacy bounded ScrollArea so the page
  // doesn't grow taller than the viewport on long lists.
  return fillHeight ? listStack : (
    <ScrollArea.Autosize mah={720} type="hover">
      {listStack}
    </ScrollArea.Autosize>
  );
}

function ThreadRow({
  t,
  selected,
  navigateOnClick,
  onSelect,
  rowIndex,
}: {
  t: InboxThread;
  selected: boolean;
  navigateOnClick: boolean;
  onSelect: (id: string) => void;
  rowIndex: number;
}) {
  const reduce = useReducedMotion();
  const isUnread = t.unreadCount > 0;
  const emitClick = (): void => {
    track('inbox.thread_clicked', {
      unread: isUnread,
      ageMinutes: Math.max(0, Math.round((Date.now() - t.lastMessageAt) / 60_000)),
    });
  };
  const previewText =
    t.preview === null
      ? 'No messages yet.'
      : t.preview.kind === 'system_event'
        ? t.preview.body
        : t.preview.fromSelf
          ? `You: ${t.preview.body}`
          : t.preview.body;

  // Stagger first 12 rows; everything past that fades instantly.
  const staggerDelay = rowIndex < 12 ? rowIndex * 0.03 : 0;

  const sharedRowStyle: React.CSSProperties = {
    position: 'relative',
    display: 'block',
    padding: '14px 16px',
    paddingLeft: selected ? 19 : 16,
    borderRadius: 10,
    textDecoration: 'none',
    color: 'inherit',
    background: selected
      ? 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))'
      : isUnread
        ? 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))'
        : 'transparent',
    transition: 'background 120ms ease',
    cursor: 'pointer',
  };

  const inner = (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
      <Avatar counterparty={t.counterparty} />
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Group gap={6} wrap="nowrap" align="center">
          <Text size="sm" fw={isUnread ? 600 : 500} truncate style={{ flex: 1, minWidth: 0 }}>
            {t.counterparty.name}
          </Text>
          <StateBadge
            entity="exchange"
            state={t.exchangeState as AnyState['exchange']}
            size="xs"
          />
        </Group>
        {t.counterparty.kind === 'artist' && <ArtistTriageChips counterparty={t.counterparty} />}
        <Text size="xs" c="ink.6" truncate fw={isUnread ? 500 : 400}>
          {previewText}
        </Text>
        <Text size="xs" c="ink.5">
          {formatDateRange(t.startDate, t.endDate)}
        </Text>
      </Stack>
      <Stack gap={4} align="flex-end">
        <Text size="xs" c="ink.5">
          {formatRelative(t.lastMessageAt)}
        </Text>
        {isUnread && (
          <Badge size="xs" variant="filled" color="brand">
            {t.unreadCount}
          </Badge>
        )}
      </Stack>
    </Group>
  );

  const railStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 6,
    bottom: 6,
    width: 3,
    borderRadius: 2,
    background: 'linear-gradient(180deg, var(--mantine-color-brand-6), var(--mantine-color-sun-5))',
  };

  // Hover background — applied on a wrapper since inline style can't do :hover.
  // We use a Mantine `data-row` attr + a tiny embedded <style> to keep the
  // file self-contained without leaking globals.
  const hoverBg = !selected && !isUnread;

  // Box wraps the link/button so we can animate entry without nesting <a> tags
  // weirdly.
  const motionInitial = reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 4 };
  const motionAnimate = { opacity: 1, y: 0 };
  const motionTransition = {
    duration: 0.32,
    ease: 'easeOut' as const,
    delay: reduce ? 0 : staggerDelay,
  };

  if (navigateOnClick) {
    return (
      <motion.div
        initial={motionInitial}
        animate={motionAnimate}
        transition={motionTransition}
        data-inbox-row={hoverBg ? 'hover' : 'static'}
        style={{ position: 'relative' }}
      >
        {selected && <span aria-hidden style={railStyle} />}
        <Link
          to="/inbox/$threadId"
          params={{ threadId: t.threadId }}
          style={sharedRowStyle}
          aria-current={selected ? 'true' : undefined}
          onClick={emitClick}
        >
          {inner}
        </Link>
        <RowHoverStyle />
      </motion.div>
    );
  }
  // Desktop two-pane: button-style row, no navigation.
  return (
    <motion.div
      initial={motionInitial}
      animate={motionAnimate}
      transition={motionTransition}
      data-inbox-row={hoverBg ? 'hover' : 'static'}
      style={{ position: 'relative' }}
    >
      {selected && <span aria-hidden style={railStyle} />}
      <Box
        component="button"
        type="button"
        onClick={() => {
          emitClick();
          onSelect(t.threadId);
        }}
        aria-current={selected ? 'true' : undefined}
        style={{
          ...sharedRowStyle,
          width: '100%',
          textAlign: 'left',
          border: 'none',
          font: 'inherit',
        }}
      >
        {inner}
      </Box>
      <RowHoverStyle />
    </motion.div>
  );
}

/**
 * Inline <style> for hover background. Scoped to `[data-inbox-row="hover"]`
 * so the static (selected/unread) rows aren't disturbed. Rendered alongside
 * each row — multiple identical <style> nodes are deduped by the browser
 * cheaply.
 */
function RowHoverStyle() {
  return (
    <style>{`[data-inbox-row="hover"]:hover > a, [data-inbox-row="hover"]:hover > button { background: var(--mantine-color-ink-0); }`}</style>
  );
}

/**
 * Avatar — 36px circular bubble with the counterparty's initial. Background
 * is a brand-tinted gradient driven by a stable hash of the counterparty id
 * (or name fallback for venues), so the same user always reads the same
 * colour. The platform doesn't return profile photos on the inbox payload
 * (and we won't N+1 to fetch them), so the initial-bubble is the universal
 * pattern.
 */
function Avatar({ counterparty }: { counterparty: InboxThread['counterparty'] }) {
  const name = counterparty.name;
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  const seed =
    counterparty.kind === 'venue'
      ? counterparty.venueId
      : counterparty.userId.length > 0
        ? counterparty.userId
        : name;
  // Venues bias to the deeper-ink palette; artists bias to brand/sun/teal.
  const palette =
    counterparty.kind === 'venue'
      ? AVATAR_PALETTES.venue
      : AVATAR_PALETTES.artist;
  const idx = stableHash(seed) % palette.length;
  const tone = palette[idx]!;
  return (
    <Box
      aria-hidden
      style={{
        flex: '0 0 auto',
        width: 36,
        height: 36,
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tone.background,
        color: tone.fg,
        fontWeight: 600,
        fontSize: 14,
        letterSpacing: '-0.01em',
        boxShadow: '0 0 0 1px rgba(255,255,255,0.4) inset',
      }}
    >
      {initial}
    </Box>
  );
}

type Tone = { background: string; fg: string };

const AVATAR_PALETTES: { artist: ReadonlyArray<Tone>; venue: ReadonlyArray<Tone> } = {
  artist: [
    {
      // brand orange
      background:
        'linear-gradient(135deg, var(--mantine-color-brand-5), var(--mantine-color-brand-7))',
      fg: '#ffffff',
    },
    {
      // sun yellow
      background:
        'linear-gradient(135deg, var(--mantine-color-sun-4), var(--mantine-color-sun-6))',
      fg: '#ffffff',
    },
    {
      // tide (brand teal — harmonises with the warm palette)
      background:
        'linear-gradient(135deg, var(--mantine-color-tide-4), var(--mantine-color-tide-7))',
      fg: '#ffffff',
    },
    {
      // ink-deep
      background:
        'linear-gradient(135deg, var(--mantine-color-ink-6), var(--mantine-color-ink-8))',
      fg: '#ffffff',
    },
  ],
  venue: [
    {
      // ink dominant for venues — read as "place" not "person"
      background:
        'linear-gradient(135deg, var(--mantine-color-ink-7), var(--mantine-color-ink-9))',
      fg: '#ffffff',
    },
    {
      background:
        'linear-gradient(135deg, var(--mantine-color-ink-5), var(--mantine-color-ink-8))',
      fg: '#ffffff',
    },
    {
      // muted brand for variety
      background:
        'linear-gradient(135deg, var(--mantine-color-brand-7), var(--mantine-color-ink-8))',
      fg: '#ffffff',
    },
  ],
};

function stableHash(input: string): number {
  // djb2 — fine for palette dispersion; no crypto needed.
  let h = 5381;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * CFR-012: chip-row under the artist name in the host inbox. Renders a small
 * verified badge, a completed-exchange tally, an aggregate review score with
 * count, and up to three art-form badges. All four are optional — empty
 * triages collapse to nothing, so artists with no track record don't push the
 * preview line down.
 */
function ArtistTriageChips({
  counterparty,
}: {
  counterparty: Extract<InboxThread['counterparty'], { kind: 'artist' }>;
}) {
  const verified = counterparty.artistVerified === true;
  const completed = counterparty.artistCompletedExchanges ?? 0;
  const avg = counterparty.artistAggregateAvg ?? null;
  const reviewCount = counterparty.artistAggregateReviewCount ?? 0;
  const artForms = counterparty.artistArtForms ?? [];
  // Hide the row entirely if there's nothing to show — keeps fresh artists
  // from getting a visual penalty.
  if (
    !verified &&
    completed === 0 &&
    (avg === null || reviewCount === 0) &&
    artForms.length === 0
  ) {
    return null;
  }
  return (
    <Group gap={4} wrap="wrap" mt={2}>
      {verified && (
        <Badge size="xs" variant="light" color="brand">
          Verified
        </Badge>
      )}
      {completed > 0 && (
        <Badge size="xs" variant="light" color="ink">
          {completed === 1 ? '1 exchange' : `${completed} exchanges`}
        </Badge>
      )}
      {avg !== null && reviewCount > 0 && (
        <Badge
          size="xs"
          variant="light"
          color="ink"
          leftSection={<StarIcon />}
        >
          {`${avg.toFixed(1)} (${reviewCount === 1 ? '1 review' : `${reviewCount} reviews`})`}
        </Badge>
      )}
      {artForms.map((name) => (
        <Badge key={name} size="xs" variant="default" color="gray">
          {name}
        </Badge>
      ))}
    </Group>
  );
}

export const Route = createFileRoute('/inbox/')({
  validateSearch: (search: Record<string, unknown>): InboxSearch => {
    const out: InboxSearch = {};
    if (typeof search.t === 'string' && search.t.length > 0) {
      out.t = search.t;
    }
    if (
      typeof search.f === 'string' &&
      (FILTER_VALUES as ReadonlyArray<string>).includes(search.f)
    ) {
      out.f = search.f as FilterValue;
    }
    return out;
  },
  component: InboxList,
});
